<template>
	<Modal v-model:visible="visible" :title="$t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.header')">
		<Form
			:model="formState"
			:label-col="{ span: 12 }"
			:wrapper-col="{ span: 12 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.cnetNumberLabel')"
				name="cnet_number"
				v-bind="validateInfos.cnetNumber"
			>
				<Select
					class="ui-select"
					v-model:value="formState.cnet_number"
					:options="cnetNumberOptions"
					:loading="isLoadingCNetList"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.weekLabel')"
				name="week"
				v-bind="validateInfos.week"
			>
				<DatePicker
					class="ui-select"
					v-model:value="formState.week"
					format="YYYY-MM-DD"
					@keyup.enter="onOk"
				/>
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" @click="onOk">{{ $t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, watch, toRef } from 'vue';
import { useVModel } from '@vueuse/core';
import { Modal, Form, FormItem, Select, DatePicker, Button, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Validators from '@/api/Validators';

import { applyFieldErrors, runValidatedSubmit } from '@/components/common/formModal';
import { dayJsToString, dateStrToDayJs, warningIfNotUtcMidnight } from '@/components/common/dateUtils';

import { useCNetNumberSelectData } from '@/components/common/controls/useCNetNumberSelectData';

export default {
	components: {
		Modal, Form, FormItem, Select, DatePicker, Button,
	},
	props: {
		modelValue: Boolean,
		fetchSettings: Function,
		grid: Object,
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();

		const visible = useVModel(props, 'modelValue', emit);

		const initialCnetNumber = ref(null);

		const formState = reactive({
			cnet_number: null,
			week: null,
		});

		const {
			resetFields,
			validate,
			validateInfos,
		} = Validators.useCNetUpdateFormRules(formState);

		const isSubmitting = ref(false);

		const gridRef = toRef(props, 'grid');

		const { isLoadingCNetList, cnetNumberOptions, loadCNetNumbers } = useCNetNumberSelectData(initialCnetNumber);

		watch(visible, (value) => {
			if (value) {
				resetFields();
				formState.cnet_number = gridRef.value.cnet_number;
				initialCnetNumber.value = gridRef.value.cnet_number;
				formState.week = gridRef.value.week ? dateStrToDayJs(gridRef.value.week) : null;
				warningIfNotUtcMidnight(formState.week, 'week', 'CNetUpdateModal');
				isSubmitting.value = false;
				loadCNetNumbers();
			}
		});

		const onOk = () => {
			runValidatedSubmit(validate, isSubmitting, () => {
				Api.subNetworkCNetUpdate(gridRef.value.id, formState.cnet_number, dayJsToString(formState.week), response => {
					if (response.status === 'ok') {
						message.success(t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.success'));
						visible.value = false;
						props.fetchSettings();
					} else {
						applyFieldErrors(validateInfos, response.errors, ['cnet_number', 'week']);
					}
					isSubmitting.value = false;
				});
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			visible,
			formState,
			validateInfos,
			isSubmitting,
			isLoadingCNetList,
			cnetNumberOptions,
			onOk,
			onCancel,
		};
	},
};
</script>

<style scoped>
.ui-select {
	min-width: 150px;
}
</style>
